import { useEffect, useState } from 'react';

import { Box } from '#/components/Box/Box';
import { Button } from '#/components/Button/Button';
import { LegacySecondaryButton } from '#/components/LegacyButton/LegacySecondaryButton';
import { StyledNativeLinkWithRef } from '#/components/Link/StyledNativeLinkWithRef';
import { LoadingSection } from '#/components/LoadingSection/LoadingSection';
import { ShareURLButton } from '#/components/ShareURLButton/ShareURLButton';
import { H2 } from '#/components/Text/H2';
import { RichTextHeadline } from '#/components/Text/RichTextHeadline';
import { Underline } from '#/components/Text/Underline';
import { useI18n } from '#/i18n/hooks/useI18n';
import { I18nLocale } from '#/i18n/types/I18nLocale';
import type { CmsApiRecentSalariesBlockDataWithAnalytics } from '#/modules/cms/api/types/blocks/CmsApiRecentSalariesBlock';
import { salariesFetchRecentSubmissions } from '#/modules/salaries/api/salariesFetchRecentSubmissions';
import { salariesSurveyRoute } from '#/modules/salaries/routing/salaries.routes';
import type { SalariesRecentSubmission } from '#/modules/salaries/types/SalariesRecentSubmission';
import { colors } from '#/theme/theme';
import { trackAnchorClicked } from '#/utils/analytics/track/trackAnchorClicked';

import {
  RecentSubmissionsContainer,
  RecentSubmissionsHeading,
  RecentSubmissionsWrapper,
  SurveyButtonContainer,
  TableBody,
  TableBodyContainer,
  TableCell,
  TableContainer,
  TableHeading,
  aCycle,
} from './RecentSubmissions.styled';

function formatSalary({
  salary,
  locale,
}: {
  salary: number;
  locale: I18nLocale;
}): string {
  return salary.toLocaleString(locale, {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
  });
}

function useSalaryRecentSubmissions() {
  const [recentSubmissions, setRecentSubmissions] =
    useState<SalariesRecentSubmission[]>();

  useEffect(() => {
    if (!recentSubmissions) {
      salariesFetchRecentSubmissions().then(
        (fetchedRecentSubmissions: SalariesRecentSubmission[]) =>
          setRecentSubmissions(fetchedRecentSubmissions),
      );
    }
  }, [recentSubmissions]);

  return recentSubmissions;
}

type RecentSubmissionsProps = {
  alreadySubmitted: boolean;
  surveyLoading: boolean;
  cmsData?: CmsApiRecentSalariesBlockDataWithAnalytics;
};

export function RecentSubmissions({
  alreadySubmitted,
  surveyLoading,
  cmsData,
}: RecentSubmissionsProps) {
  const { i18n, locale } = useI18n();

  const recentSubmissions = useSalaryRecentSubmissions();

  const tableHeading = i18n('{{s}}Recent Salary Submissions{{/s}}', {
    s: <Underline />,
  });

  const shareUrl = {
    en: salariesSurveyRoute.fullPath,
    es: salariesSurveyRoute.fullPath,
    pt: salariesSurveyRoute.fullPath,
  };

  const messageOverride = i18n(
    'I just took the Idealist Salary Survey! Find out what you could be earning at a nonprofit in just a few quick steps.',
  );
  const emailMessageOverride = i18n(
    'I just took the Idealist Salary Survey! Find out what you could be earning at a nonprofit in just a few quick steps. Start here:',
  );
  const emailSubjectOverride = i18n('Check this out: Idealist Salaries');

  const data = recentSubmissions
    ? recentSubmissions
        .concat(recentSubmissions.slice(0, 4))
        .map((submission) => [
          submission.title,
          formatSalary({ salary: submission.salary, locale }),
          submission.location,
        ])
    : [];

  return (
    <RecentSubmissionsContainer>
      <RecentSubmissionsWrapper>
        <RecentSubmissionsHeading>
          {cmsData ? (
            <RichTextHeadline>{cmsData.headline}</RichTextHeadline>
          ) : (
            <H2>
              {i18n('Help us research pay equity in the nonprofit sector')}
            </H2>
          )}

          {cmsData && (
            <LegacySecondaryButton
              component={StyledNativeLinkWithRef}
              href={cmsData.buttonUrl}
              $reverse
              onClick={(event) =>
                trackAnchorClicked(event, 'CMS Block', {
                  block_position: cmsData.blockIndex,
                  block_type: 'Recent Salaries',
                  cta_type: 'Button',
                  button_text: cmsData.buttonText,
                  page_updated: cmsData.pageUpdated,
                })
              }
            >
              {cmsData.buttonText}
            </LegacySecondaryButton>
          )}

          {!cmsData && !surveyLoading && (
            <SurveyButtonContainer>
              {alreadySubmitted ? (
                <ShareURLButton
                  buttonText="Share Our Survey"
                  shareUrl={shareUrl}
                  variant="secondary-white"
                  messageOverride={messageOverride}
                  emailMessageOverride={emailMessageOverride}
                  emailSubjectOverride={emailSubjectOverride}
                />
              ) : (
                <Button
                  type="link"
                  href={salariesSurveyRoute.fullPath}
                  variant="secondary-white"
                >
                  {i18n('Take Our Survey')}
                </Button>
              )}
            </SurveyButtonContainer>
          )}
        </RecentSubmissionsHeading>

        <TableContainer>
          <TableHeading>{tableHeading}</TableHeading>
          {!recentSubmissions ? (
            <LoadingSection />
          ) : (
            <>
              <Box
                display="flex"
                fontWeight={600}
                backgroundColor={colors.backgroundGrey}
              >
                <TableCell
                  style={{ width: '65%', color: colors.lightContentGrey }}
                >
                  {i18n('Position')}
                </TableCell>
                <TableCell
                  style={{ width: '15%', color: colors.lightContentGrey }}
                >
                  {i18n('Salary')}
                </TableCell>
                <TableCell
                  style={{ width: '20%', color: colors.lightContentGrey }}
                >
                  {i18n('Location')}
                </TableCell>
              </Box>

              <TableBodyContainer>
                <TableBody
                  $animationName={aCycle(data)}
                  $itemCount={data.length}
                >
                  {data.map((item, i) => (
                    <Box key={[...item, i].join()} display="flex">
                      <TableCell style={{ width: '65%' }}>{item[0]}</TableCell>
                      <TableCell style={{ width: '15%' }}>{item[1]}</TableCell>
                      <TableCell style={{ width: '20%' }}>{item[2]}</TableCell>
                    </Box>
                  ))}
                </TableBody>
              </TableBodyContainer>
            </>
          )}
        </TableContainer>
      </RecentSubmissionsWrapper>
    </RecentSubmissionsContainer>
  );
}
